import { useState } from "react";

interface Props {
  adultMode: boolean;
  user: { email?: string | null } | null;
  onSwitchMode: () => void;
  onChangePath: () => void;
  onAccount: () => void;
  onSignOut: () => void;
  onFeedback: () => void;
  isAdmin?: boolean;
  onAdmin?: () => void;
}

export default function HamburgerMenu({
  adultMode,
  user,
  onSwitchMode,
  onChangePath,
  onAccount,
  onSignOut,
  onFeedback,
  isAdmin,
  onAdmin,
}: Props) {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  const switchGradient = adultMode
    ? "linear-gradient(135deg,#f97316,#ec4899)"
    : "linear-gradient(135deg,#0ea5e9,#8b5cf6)";

  return (
    <>
      {/* Hamburger trigger */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Open menu"
        style={{
          position: "fixed",
          top: 12,
          right: 12,
          zIndex: 60,
          width: 40,
          height: 40,
          borderRadius: 10,
          background: "rgba(255,255,255,0.95)",
          border: "none",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 5,
          boxShadow: "0 2px 10px rgba(0,0,0,0.18)",
          flexShrink: 0,
        }}
      >
        <Bar />
        <Bar />
        <Bar />
      </button>

      {/* Backdrop */}
      <div
        onClick={close}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.45)",
          zIndex: 70,
          pointerEvents: open ? "auto" : "none",
          opacity: open ? 1 : 0,
          transition: "opacity 0.22s ease",
        }}
      />

      {/* Slide-in panel */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: 290,
          background: "#fff",
          zIndex: 80,
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 0.26s cubic-bezier(0.4, 0, 0.2, 1)",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          boxShadow: "-6px 0 32px rgba(0,0,0,0.18)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "18px 16px 14px",
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a", letterSpacing: -0.3 }}>
              MoneyMission
            </div>
            <div style={{ fontSize: 11, color: "#64748b", marginTop: 1 }}>
              {adultMode ? "Pro Mode" : "Kids Mode"}
            </div>
          </div>
          <button
            onClick={close}
            aria-label="Close menu"
            style={{
              width: 32,
              height: 32,
              borderRadius: 8,
              border: "none",
              background: "#f1f5f9",
              cursor: "pointer",
              fontSize: 18,
              color: "#475569",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* User info */}
        {user && (
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid #e2e8f0",
              background: "#f8fafc",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "linear-gradient(135deg,#0ea5e9,#8b5cf6)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 16,
                flexShrink: 0,
              }}
            >
              👤
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: "#0f172a",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {user.email}
              </div>
              <div style={{ fontSize: 10, color: "#64748b" }}>Signed in</div>
            </div>
          </div>
        )}

        {/* Menu body */}
        <div style={{ flex: 1, padding: "6px 0" }}>
          {/* Switch mode */}
          <MenuItem
            icon={adultMode ? "🎮" : "💼"}
            label={adultMode ? "Switch to Kids" : "Switch to Pro"}
            iconBg={switchGradient}
            onClick={() => { onSwitchMode(); close(); }}
          />

          {/* Change path */}
          <MenuItem
            icon="🏠"
            label="Change Path"
            onClick={() => { onChangePath(); close(); }}
          />

          <Divider />

          {/* Account & billing (any logged-in user) */}
          {user && (
            <>
              <MenuItem
                icon="⚙️"
                label="Manage Account"
                onClick={() => { onAccount(); close(); }}
              />
              <MenuItem
                icon="💳"
                label="Payments & Billing"
                onClick={() => { onAccount(); close(); }}
              />
            </>
          )}

          {/* Sign out (kids, logged in) */}
          {user && !adultMode && (
            <MenuItem
              icon="🔓"
              label="Sign Out"
              onClick={() => { onSignOut(); close(); }}
            />
          )}

          <Divider />

          <MenuItem
            icon="💬"
            label="Send Feedback"
            onClick={() => { onFeedback(); close(); }}
          />

          {isAdmin && onAdmin && (
            <>
              <Divider />
              <MenuItem
                icon="🔧"
                label="Admin"
                iconBg="linear-gradient(135deg,#7c3aed,#0ea5e9)"
                onClick={() => { onAdmin(); close(); }}
              />
            </>
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "14px 16px",
            borderTop: "1px solid #e2e8f0",
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: 10, color: "#94a3b8" }}>MoneyMission © 2026</div>
          <div style={{ fontSize: 10, color: "#94a3b8", marginTop: 2 }}>
            <a href="https://moneymission.app/privacy" target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "none" }}>Privacy</a>
            {" · "}
            <a href="https://moneymission.app/terms" target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "none" }}>Terms</a>
          </div>
        </div>
      </div>
    </>
  );
}

function Bar() {
  return (
    <span
      style={{
        display: "block",
        width: 18,
        height: 2,
        background: "#334155",
        borderRadius: 2,
      }}
    />
  );
}

function Divider() {
  return (
    <div style={{ height: 1, background: "#e2e8f0", margin: "6px 16px" }} />
  );
}

function MenuItem({
  icon,
  label,
  onClick,
  iconBg,
}: {
  icon: string;
  label: string;
  onClick: () => void;
  iconBg?: string;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        width: "100%",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "11px 16px",
        background: "none",
        border: "none",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span
        style={{
          width: 34,
          height: 34,
          borderRadius: 9,
          background: iconBg || "#f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 17,
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <span style={{ fontSize: 14, fontWeight: 600, color: "#0f172a" }}>
        {label}
      </span>
    </button>
  );
}
